/**
 * <s-help-center> — Help Center page behaviour (templates/page.liquid, Help Center branch).
 *
 * - Builds each document's sub-items from the headings in its body: the top heading level when it has
 *   2+ headings, otherwise every h2–h4; documents without headings fall back to bold-only lines
 *   (a short bold run that fills its paragraph or ends at a line break, e.g. "SECTION 1 - ..."). Headings get ids "<doc>--<slug>".
 * - Shows one document at a time. The URL hash picks it: "#faqs" opens a document, "#faqs--slug" opens
 *   it and scrolls to that heading. No hash opens the first document.
 * - The active document's sub-list opens; +/- buttons open or close any sub-list.
 * - On mobile the contents list is collapsible and closes after a link is chosen.
 */
// Section titles read the same in every document: no leading "1." / "SECTION 1 -" and no ALL CAPS.
const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);
const toTitleCase = (text) =>
  text
    .toLowerCase()
    .replace(/[a-z][a-z']*/g, (word, offset) => (offset > 0 && SMALL_WORDS.has(word) ? word : word[0].toUpperCase() + word.slice(1)));
const normalizeHeading = (heading) => {
  const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
  const nodes = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node);
  const first = nodes.find((node) => node.textContent.trim());
  if (first) first.textContent = first.textContent.replace(/^\s*(?:section\s+\d+\s*[-–:.]\s*|\d+[.)]\s+)/i, '');
  const text = heading.textContent;
  if (/[A-Z]/.test(text) && text === text.toUpperCase()) {
    nodes.forEach((node) => {
      node.textContent = toTitleCase(node.textContent);
    });
  }
};

const desktop = window.matchMedia('(min-width: 48rem)');

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'section';

class SHelpCenter extends HTMLElement {
  connectedCallback() {
    this.docs = [...this.querySelectorAll('[data-help-doc]')];
    this.items = new Map([...this.querySelectorAll('[data-help-item]')].map((li) => [li.dataset.helpItem, li]));
    this.toc = this.querySelector('[data-help-toc]');
    if (!this.docs.length) return;

    this.docs.forEach((doc) => this.buildSubItems(doc));

    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-help-toggle]');
      if (toggle) {
        this.setExpanded(toggle.closest('[data-help-item]'), toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const summary = event.target.closest('summary');
      if (summary && desktop.matches) event.preventDefault();
    });

    // Contents links and links elsewhere on the page (e.g. the footer's "/pages/store-policies#faqs") that point
    // at a document here switch documents in place, instead of relying on the browser's hash jump (which does
    // nothing when the hash is already current, and is blocked inside some previews).
    // Capture phase on window so this runs before preview tooling that handles link clicks itself.
    window.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      // Compare the last path segment so a locale prefix or preview path on either side still matches.
      const lastSegment = (path) => path.replace(/\/+$/, '').split('/').pop();
      if (lastSegment(url.pathname) !== lastSegment(location.pathname) || !url.hash) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target || !this.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (location.hash !== url.hash) history.pushState(null, '', url.hash);
      this.route(true);
      if (!desktop.matches && this.toc && link.closest('[data-help-toc]')) this.toc.open = false;
    }, true);

    window.addEventListener('hashchange', () => this.route(true));
    window.addEventListener('popstate', () => this.route(true));
    desktop.addEventListener('change', () => this.syncToc());
    this.syncToc();
    this.route(Boolean(location.hash));
  }

  syncToc() {
    if (this.toc) this.toc.open = desktop.matches;
  }

  buildSubItems(doc) {
    const body = doc.querySelector('[data-help-body]');
    const item = this.items.get(doc.id);
    if (!body || !item) return;
    const sub = item.querySelector('[data-help-sub]');
    const toggle = item.querySelector('[data-help-toggle]');

    const all = [...body.querySelectorAll('h2, h3, h4')].filter((h) => h.textContent.trim());
    let headings = all;
    if (all.length) {
      const top = Math.min(...all.map((h) => Number(h.tagName[1])));
      const topOnly = all.filter((h) => Number(h.tagName[1]) === top);
      if (topOnly.length >= 2) headings = topOnly;
    } else {
      const isBreakAfter = (el) => {
        let next = el.nextSibling;
        while (next && next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) next = next.nextSibling;
        return !next || next.nodeName === 'BR';
      };
      headings = [...body.querySelectorAll('p strong, p b')].filter((strong) => {
        const text = strong.textContent.trim();
        if (!text || text.length > 90) return false;
        const p = strong.closest('p');
        return p.textContent.trim() === text || isBreakAfter(strong);
      });
    }

    const used = new Set();
    headings.forEach((heading) => {
      let id = `${doc.id}--${slugify(heading.textContent)}`;
      while (used.has(id)) id += '-2';
      used.add(id);
      heading.id = id;
      heading.classList.add('scroll-mt-28');
      // Section headings in the contents list share one size, whatever heading level the content uses.
      if (/^H[2-6]$/.test(heading.tagName)) heading.dataset.helpSection = '';
      normalizeHeading(heading);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${id}`;
      a.textContent = heading.textContent.trim();
      a.className = 'block py-1 hover:text-on-surface aria-[current=true]:text-accent';
      li.append(a);
      sub.append(li);
    });

    if (headings.length && toggle) toggle.hidden = false;
  }

  setExpanded(item, open) {
    if (!item) return;
    const toggle = item.querySelector('[data-help-toggle]');
    const sub = item.querySelector('[data-help-sub]');
    if (!toggle || toggle.hidden || !sub) return;
    toggle.setAttribute('aria-expanded', String(open));
    sub.hidden = !open;
  }

  route(scroll) {
    const hash = decodeURIComponent(location.hash.slice(1));
    let target = hash ? document.getElementById(hash) : null;
    // After sending the contact form, reopen Contact Us so the success or error message is visible.
    if (!target) target = this.querySelector('[data-help-open]');
    let doc = target ? target.closest('[data-help-doc]') : null;
    if (!doc || !this.contains(doc)) {
      doc = this.docs[0];
      target = null;
    }

    this.docs.forEach((d) => (d.hidden = d !== doc));
    this.items.forEach((item, id) => {
      const active = id === doc.id;
      item.querySelector('[data-help-link]')?.setAttribute('aria-current', String(active));
      if (active) this.setExpanded(item, true);
    });
    const current = this.querySelector('[data-help-current]');
    const activeLink = this.items.get(doc.id)?.querySelector('[data-help-link]');
    if (current && activeLink) current.textContent = activeLink.textContent.trim();
    this.querySelectorAll('[data-help-sub] a').forEach((a) => {
      a.setAttribute('aria-current', String(target !== null && a.getAttribute('href') === `#${target.id}`));
    });

    if (!scroll) return;
    const scrollTarget = target && target !== doc ? target : doc;
    requestAnimationFrame(() => scrollTarget.scrollIntoView({ block: 'start', behavior: 'smooth' }));
  }
}

if (!customElements.get('s-help-center')) customElements.define('s-help-center', SHelpCenter);
